import React, { useState, useRef, useEffect } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

// Navigation Component
export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <div className="flex items-center">
              <div className="w-14 h-14 mr-3">
                <img 
                  src="/images/HLVLogo.svg" 
                  alt="Harmony Living by V Logo" 
                  className="w-full h-full"
                />
              </div>
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-coastal-blue font-serif">Harmony Living by V</h1>
              </div>
            </div>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-6">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">Testimonials</button>
              <button onClick={() => scrollToSection('living-in-harmony')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">Articles</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-coastal-blue transition-colors font-medium">FAQ</button>
              
              {/* Phone Number */}
              <div className="flex items-center">
                <a 
                  href="tel:+18668843970" 
                  className="flex items-center text-coastal-blue hover:text-coastal-dark transition-colors font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  (866) 884-3970
                </a>
              </div>
              
              <button onClick={() => scrollToSection('contact')} className="bg-coastal-blue text-white px-4 py-2 rounded-full hover:bg-coastal-dark transition-colors font-medium">Free Consultation</button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700 hover:text-rose-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              <button onClick={() => scrollToSection('home')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">Home</button>
              <button onClick={() => scrollToSection('about')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">About</button>
              <button onClick={() => scrollToSection('services')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">Testimonials</button>
              <button onClick={() => scrollToSection('living-in-harmony')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">Articles</button>
              <button onClick={() => scrollToSection('faq')} className="block text-gray-700 hover:text-coastal-blue px-3 py-2 font-medium w-full text-left">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block bg-coastal-blue text-white px-3 py-2 rounded-lg hover:bg-coastal-dark font-medium w-full text-left">Free Consultation</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section Component
export const HeroSection = () => {
  return (
    <section id="home" className="min-h-screen flex items-center bg-gradient-to-br from-fog via-shell to-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {/* Logo Integration */}
            <div className="flex justify-center lg:justify-start mb-6">
              <div className="w-24 h-24">
                <img 
                  src="/images/HLVLogo.svg" 
                  alt="Harmony Living by V Logo" 
                  className="w-full h-full drop-shadow-lg"
                />
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-800 leading-tight">
              <span className="block font-serif italic text-coastal-blue">"Reaching out is a</span>
              <span className="block font-serif italic text-gray-700">step toward</span>
              <span className="block font-serif italic text-coastal-blue">living your best life!"</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 font-serif italic leading-relaxed mb-6">
              "Allow me to assist, support and take the pressure off your work, personal and home life"
            </p>
            
            <div className="bg-coastal-blue/10 p-6 rounded-xl border-l-4 border-coastal-blue">
              <p className="text-lg text-gray-700 leading-relaxed">
                Transform your home into a harmonious sanctuary with professional organization services 
                that truly work for your lifestyle.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-coastal-blue text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-coastal-dark transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Get Your Free Consultation
              </button>
              <button 
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-coastal-blue text-coastal-blue px-8 py-4 rounded-full text-lg font-semibold hover:bg-coastal-blue hover:text-white transition-all duration-300"
              >
                View Services
              </button>
            </div>
          </div>

          <div className="relative order-first lg:order-last">
            <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:rotate-0 transition-transform duration-500 lg:rotate-3">
              <img 
                src="https://images.unsplash.com/photo-1504977402025-84285fea814b" 
                alt="Beautiful organized living space showing harmony and transformation" 
                className="w-full h-64 sm:h-80 lg:h-96 xl:h-[500px] object-cover object-center"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white p-3 sm:p-4 lg:p-6 rounded-xl shadow-lg">
              <p className="text-xs sm:text-sm text-gray-600 font-medium">Transform Your Space</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-coastal-blue">Create Harmony</p>
            </div>
            <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 bg-coastal-blue text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full shadow-lg">
              <p className="text-xs sm:text-sm font-semibold">Professional</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section Component
export const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            Meet V - Los Angeles & Orange County's Premier Professional Organizer
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            For over 30 years, we have helped families, professionals, and households of all kinds get organized with a warm Australian touch across LA and Orange County.
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          <div className="space-y-6 order-last lg:order-first">
            <div className="bg-sand-light p-6 rounded-xl border-l-4 border-coastal-blue">
              <p className="text-lg text-gray-700 leading-relaxed italic">
                "Originally from Australia, I've called California home for nearly 30 years. My unique perspective combines 
                the relaxed Australian lifestyle with Southern California coastal living - creating harmonious spaces that truly work for busy lives."
              </p>
            </div>
            
            <div className="bg-coastal-blue/5 p-6 rounded-xl">
              <h3 className="text-2xl font-semibold text-coastal-dark mb-4">My Journey & Professional Experience</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>For the past 30 years</strong>, I have helped households of all kinds create organized, functional, and welcoming spaces throughout Los Angeles County and Orange County. From family homes and cozy apartments to expansive estates, my work reflects each client's lifestyle and needs.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>My expertise spans four comprehensive services:</strong> Move-In & Move-Out Support, Home Transition & Estate Organization, Professional Organization and Personal Assistant Services. This combination provides a complete lifestyle solution that goes beyond organization alone.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong> My mission has always been simple: <em>creating harmony in their lives and making everything completely stress-free.</em> </strong>
                {' '}I've helped families settle comfortably into new homes, guided seniors through the process of downsizing, organized home offices for busy professionals, refreshed wardrobes and living spaces, and created functional, welcoming environments for households across communities from Beverly Hills to Huntington Beach.
                </p>
            </div>

            {/* Authority & Expertise Section */}
            <div className="bg-gradient-to-r from-coastal-blue/10 to-sand-light p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-coastal-dark mb-3">Why Choose V as Your Professional Organizer?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-coastal-blue mr-2">✓</span>
                  <span><strong>30 years of proven experience</strong> serving clients through every step of their life journey across LA & Orange County</span>
                </li>
                <li className="flex items-start">
                  <span className="text-coastal-blue mr-2">✓</span>
                  <span><strong>Australian heritage approach</strong> - combining relaxed lifestyle principles with California efficiency</span>
                </li>
                <li className="flex items-start">
                  <span className="text-coastal-blue mr-2">✓</span>
                  <span><strong>Comprehensive lifestyle services</strong> - the only organizer offering complete 4-service solutions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-coastal-blue mr-2">✓</span>
                  <span><strong>Discretion and confidentiality</strong> - valued by clients from all walks of life in Southern California</span>
                </li>
                <li className="flex items-start">
                  <span className="text-coastal-blue mr-2">✓</span>
                  <span><strong>Serving all of LA County & Orange County</strong> - Beverly Hills, Irvine, Huntington Beach, Pasadena & more</span>
                </li>
              </ul>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed font-semibold text-coastal-dark bg-shell p-4 rounded-xl">
              I believe everyone deserves to wake up to a home that brings peace and serenity. Let me help transform 
              your daily life into a walk in the park - just as I've done for countless satisfied clients over the past 30 years.
              <em className="block mt-2 text-base font-normal">"Reaching out is a step toward living your best life!" - V</em>
            </p>
          </div>

          <div className="relative order-first lg:order-last">
            {/* Main Professional Photo */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-4 sm:mb-6">
              <img 
                src="/images/vezira-professional.jpg" 
                alt="V - Professional Organizer Los Angeles Orange County - 30 years experience serving celebrities and luxury homes" 
                className="w-full h-80 sm:h-96 md:h-[420px] lg:h-[480px] xl:h-[520px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coastal-blue/20 to-transparent"></div>
              <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 lg:bottom-8 lg:left-8 bg-white/95 p-3 sm:p-4 lg:p-6 xl:p-7 rounded-lg shadow-lg max-w-[180px] sm:max-w-[220px] lg:max-w-[280px] xl:max-w-[320px]">
                <p className="text-xs sm:text-sm lg:text-base xl:text-lg text-gray-600 font-medium">Originally from Australia</p>
                <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-coastal-blue leading-tight">30 Years in California</p>
                <p className="text-xs sm:text-sm lg:text-sm xl:text-base text-gray-500 mt-1">Professional Home Organizer for Every Lifestyle</p>
              </div>
            </div>
            
            {/* Service Images Grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
              <img 
                src="https://images.unsplash.com/photo-1577020914435-7ae6b1091ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxvcmdhbml6ZWQlMjBjbG9zZXR8ZW58MHx8fHwxNzUwMzAxNDE2fDA&ixlib=rb-4.1.0&q=85" 
                alt="Professional closet organization Los Angeles" 
                className="w-full h-20 sm:h-24 lg:h-32 xl:h-36 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
              />
              <img 
                src="https://images.pexels.com/photos/8580742/pexels-photo-8580742.jpeg" 
                alt="Kitchen organization Orange County" 
                className="w-full h-20 sm:h-24 lg:h-32 xl:h-36 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
              />
              <img 
                src="https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxldmVudCUyMHBsYW5uaW5nfGVufDB8fHx8MTc1MDMwMTQzMXww&ixlib=rb-4.1.0&q=85" 
                alt="Event planning services Beverly Hills" 
                className="w-full h-20 sm:h-24 lg:h-32 xl:h-36 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section Component
export const ServicesSection = () => {
  const services = [
    {
      title: "Move-In & Move-Out Support",
      image: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtb3ZpbmclMjBib3hlc3xlbnwwfHx8fDE3NTAzMDE0MTZ8MA&ixlib=rb-4.1.0&q=85",
      description: "Hands-on help for a smoother transition, whether settling into a new home or preparing to move out",
      overview: "Moving is a major life event that can feel overwhelming, whether you're a homeowner, renter, or real estate professional. Harmony Living By V offers Move-In & Move-Out Support to make your transition easier, more organized, and less stressful. We take care of the details, so you can focus on your next chapter.",
      whatWeOffer: [
        {
          title: "Decluttering and Organization",
          description: "We help you sort through your belongings, making it easier to decide what to keep, donate, or discard before you move."
        },
        {
          title: "Packing Assistance",
          description: "Our team provides efficient and careful packing, ensuring your items are protected and ready for transport."
        },
        {
          title: "Unpacking and Set-Up",
          description: "We can help you unpack and set up your new space, organizing your home for function and comfort from day one."
        },
        {
          title: "Home Staging and Preparation",
          description: "If you're selling or renting, we'll help declutter and stage your property to make it as appealing as possible to buyers or tenants."
        },
        {
          title: "Donation and Disposal Coordination",
          description: "We'll arrange for the donation or disposal of any unwanted items, connecting you with local charities and hauling services."
        },
        {
          title: "Complete Service Coordination",
          description: "Need movers, cleaners, or handymen? V serves as your single point of contact, organizing and managing all service providers for a seamless move experience."
        }
      ],
      whoIsThisFor: [
        "Homeowners preparing to sell, move, or downsize",
        "Renters transitioning in or out of a property",
        "Real estate agents seeking reliable, turnkey move support for their clients",
        "Anyone feeling overwhelmed by the logistics of moving"
      ],
      whyChoose: [
        {
          title: "Experienced and Supportive",
          description: "We know moving can be stressful—our team provides experienced, caring support every step of the way."
        },
        {
          title: "Personalized Service",
          description: "No two moves are alike. We tailor our services to your unique needs and timeline."
        },
        {
          title: "Attention to Detail",
          description: "From careful packing to thoughtful organization, we make sure nothing is overlooked."
        },
        {
          title: "Peace of Mind",
          description: "Let us handle the logistics, so you can focus on settling in and starting fresh."
        }
      ],
      areas: ["Decluttering & Organization", "Packing Assistance", "Unpacking & Set-Up", "Home Staging", "Donation Coordination", "Complete Service Coordination"],
      pricing: "Contact for personalized rates"
    },
    {
      title: "Home Transition & Estate Organization",
      image: "https://images.unsplash.com/photo-1560448205-4d9b3e6bb6db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxlc3RhdGUlMjBvcmdhbml6YXRpb258ZW58MHx8fHwxNzUwMzAxNDE2fDA&ixlib=rb-4.1.0&q=85",
      description: "Compassionate support for families during life transitions and estate organization",
      overview: "Losing a loved one is never easy. Sorting through their belongings and preparing their home can be an emotional and overwhelming task. Harmony Living By V now offers specialized support to help families with the process of organizing, sorting, and making thoughtful decisions about a loved one's home and possessions.",
      whatWeOffer: [
        {
          title: "Compassionate Sorting and Organization",
          description: "We'll work side-by-side with you to sort through personal belongings, respecting both sentimental and practical needs."
        },
        {
          title: "Guidance on What to Keep, Donate, or Discard",
          description: "We help families make decisions on which items to keep, what can be donated, and what should be discarded—always honoring your wishes and values."
        },
        {
          title: "Documentation and Inventory",
          description: "We can help create a detailed inventory or photo log of items for estate purposes, sharing, or legal needs."
        },
        {
          title: "Arranging Donations and Disposals",
          description: "We'll coordinate the donation of appropriate items to local charities and arrange for responsible disposal of unwanted items."
        },
        {
          title: "Preparing the Home for Sale or Transfer",
          description: "If needed, we can help declutter, clean, and stage the property for sale or for transfer to new occupants."
        },
        {
          title: "Resource Connections",
          description: "We can refer you to trusted local partners—estate sales, movers, cleaning services, and more—so you have a single point of contact."
        }
      ],
      whoIsThisFor: [
        "Families who have recently lost a loved one and need assistance with their home and belongings",
        "Executors or estate administrators looking for practical, hands-on help",
        "Anyone feeling overwhelmed by the emotional and physical workload that comes with a loved one's passing"
      ],
      whyChoose: [
        {
          title: "Compassionate, Respectful, and Discreet",
          description: "We understand the emotional nature of this work and treat every family and home with dignity."
        },
        {
          title: "Personalized Service",
          description: "Every family and every situation is unique—we'll tailor our support to your specific needs."
        },
        {
          title: "Peace of Mind",
          description: "Let us lighten your burden so you can focus on what matters most: remembering and honoring your loved one."
        }
      ],
      areas: ["Compassionate Sorting", "Decision Guidance", "Documentation & Inventory", "Donation Coordination", "Home Preparation", "Resource Connections"],
      pricing: "Contact for personalized rates"
    },
    {
      title: "Professional Organization",
      image: "https://images.unsplash.com/photo-1577020914435-7ae6b1091ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxvcmdhbml6ZWQlMjBjbG9zZXR8ZW58MHx8fHwxNzUwMzAxNDE2fDA&ixlib=rb-4.1.0&q=85",
      description: "Transform your living spaces with professional organizing solutions",
      areas: ["Kitchen & Pantry", "Closets & Wardrobes", "Bedrooms & Bathrooms", "Garage & Storage", "Office Spaces"],
      pricing: "Contact for personalized rates",
      pricingDescription: "Each client's situation is unique, and we tailor our pricing to meet your specific needs and circumstances."
    },
    {
      title: "Personal Assistant Services",
      image: "https://images.unsplash.com/photo-1594501252028-2bb7b21d01b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHNlYXJjaHwxfHx2aXJ0dWFsJTIwYXNzaXN0YW50JTIwd29ya3NwYWNlfGVufDB8fHx8MTc1MDMwMTQzNnww&ixlib=rb-4.1.0&q=85",
      description: "Friendly, reliable help for life's everyday tasks, provided in person or online. This service is ideal for anyone who wants to save time, stay organized, and receive trusted support. It is also perfect for those who are not tech savvy or prefer a personal touch when it comes to managing schedules and technology.",
      areas: ["Managing your calendar and important dates", "Booking travel and making reservations", "Personal shopping and gift selection", "Organizing paperwork and files", "Coordinating household services and repairs"],
      pricing: "Contact for personalized rates",
      pricingDescription: "Each client's situation is unique, and we tailor our pricing to meet your specific needs and circumstances.",
      services: ["Assistance with computer and smartphone use such as Microsoft Office, email, and online forms", "Help with setting up and managing online accounts", "Taking meeting notes and typing documents", "Arranging transportation or delivery services", "Local concierge services for errands and special requests"]
    }
  ];

  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-shell to-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            Our Professional Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive solutions to harmonize your life and create the peaceful, functional space you deserve
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              onClick={() => setSelectedService(service)}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white font-semibold text-lg">Learn More</span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                <div className="text-coastal-blue font-semibold text-lg">{service.pricing}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-gray-700 mb-6">
            Contact me for your personalized evaluation.
          </p>
          <button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-coastal-blue text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-coastal-dark transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get FREE Consultation
          </button>
        </div>

        {/* Service Modal */}
        {selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative">
                <img 
                  src={selectedService.image} 
                  alt={selectedService.title}
                  className="w-full h-64 object-cover"
                />
                <button 
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-8">
                <h3 className="text-3xl font-bold text-gray-800 mb-4">{selectedService.title}</h3>
                <p className="text-lg text-gray-600 mb-6">{selectedService.description}</p>
                
                {/* Special content for Move-In & Move-Out Support and Home Transition & Estate Organization */}
                {(selectedService.title === "Move-In & Move-Out Support" || selectedService.title === "Home Transition & Estate Organization") ? (
                  <div className="space-y-8">
                    {/* Overview Section */}
                    <div className="bg-sand-light p-6 rounded-xl border-l-4 border-coastal-blue">
                      <h4 className="text-xl font-semibold text-gray-800 mb-3">Overview</h4>
                      <p className="text-gray-700 leading-relaxed">{selectedService.overview}</p>
                    </div>

                    {/* What We Offer Section */}
                    <div>
                      <h4 className="text-2xl font-semibold text-gray-800 mb-6">What We Offer</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {selectedService.whatWeOffer.map((offer, idx) => (
                          <div key={idx} className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                            <h5 className="font-semibold text-coastal-blue mb-2">{offer.title}</h5>
                            <p className="text-gray-600 text-sm">{offer.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Who Is This For Section */}
                    <div>
                      <h4 className="text-2xl font-semibold text-gray-800 mb-4">Who Is This For?</h4>
                      <ul className="space-y-3">
                        {selectedService.whoIsThisFor.map((item, idx) => (
                          <li key={idx} className="flex items-start text-gray-700">
                            <span className="text-coastal-blue mr-3 mt-1">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Why Choose Section */}
                    <div>
                      <h4 className="text-2xl font-semibold text-gray-800 mb-4">Why Choose Harmony Living By V?</h4>
                      <div className="space-y-4">
                        {selectedService.whyChoose.map((reason, idx) => (
                          <div key={idx} className="bg-coastal-blue/5 p-4 rounded-lg">
                            <h5 className="font-semibold text-coastal-dark mb-2">{reason.title}</h5>
                            <p className="text-gray-700">{reason.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="bg-shell p-6 rounded-xl text-center">
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">Pricing</h4>
                      <p className="text-2xl font-bold text-coastal-blue mb-2">{selectedService.pricing}</p>
                      <p className="text-gray-600 text-sm">Each family's situation is unique, and we tailor our pricing to meet your specific needs and circumstances.</p>
                    </div>
                  </div>
                ) : (
                  // Regular service modal content
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">Areas We Cover:</h4>
                      <ul className="space-y-2">
                        {selectedService.areas.map((area, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <span className="text-coastal-blue mr-2">✓</span>
                            {area}
                          </li>
                        ))}
                      </ul>
                      
                      {selectedService.services && (
                        <div className="mt-6">
                          <h4 className="text-xl font-semibold text-gray-800 mb-4">Additional Services:</h4>
                          <ul className="space-y-2">
                            {selectedService.services.map((service, idx) => (
                              <li key={idx} className="flex items-center text-gray-700">
                                <span className="text-coastal-blue mr-2">•</span>
                                {service}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">Pricing:</h4>
                      <p className="text-2xl font-bold text-coastal-blue mb-4">{selectedService.pricing}</p>
                      
                      {selectedService.pricingDescription && (
                        <p className="text-gray-600 text-sm mb-4">{selectedService.pricingDescription}</p>
                      )}
                      
                      {selectedService.packages && (
                        <div className="space-y-3">
                          <h5 className="font-semibold text-gray-700">Package Options:</h5>
                          {selectedService.packages.map((pkg, idx) => (
                            <div key={idx} className="bg-coastal-blue/5 p-3 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{pkg.name}</span>
                                <span className="font-bold text-coastal-blue">{pkg.price}</span>
                              </div>
                              {pkg.savings && (
                                <div className="text-sm text-coastal-blue mt-1">{pkg.savings}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Testimonials Section Component
export const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Jessica & Mark Thompson",
      location: "Huntington Beach, CA",
      text: "V made our move from a 4-bedroom house to a condo completely stress-free! She helped us declutter, pack efficiently, and even set up our new place so beautifully. We walked into a fully organized, functional home on day one. Absolutely incredible service!",
      rating: 5,
      service: "Move-In & Move-Out Support"
    },
    {
      name: "Lisa Hall",
      location: "Beverly Hills, CA",
      text: "As a real estate agent, I've worked with many organizers, but V is exceptional. She staged my client's home perfectly for sale and then helped them with their entire move. The house sold in 3 days, and the transition was seamless. My go-to for all move support!",
      rating: 5,
      service: "Move-In & Move-Out Support"
    },
    {
      name: "Robert & Catherine Williams",
      location: "Pasadena, CA",
      text: "After losing my father, we were overwhelmed by decades of belongings and didn't know where to start. V approached everything with such compassion and understanding. She helped us make difficult decisions while honoring Dad's memory. Her gentle guidance turned an impossible task into a manageable process. We're forever grateful.",
      rating: 5,
      service: "Home Transition & Estate Organization"
    },
    {
      name: "Sarah Connor",
      location: "Beverly Hills, CA",
      text: "V transformed my chaotic kitchen into a functional masterpiece! I can finally find everything I need, and cooking has become a joy again. Her organizational system has saved me hours every week.",
      rating: 5,
      service: "Professional Organization"
    },
    {
      name: "Michael Chen",
      location: "Irvine, CA",
      text: "As a busy executive, V's personal assistant services have been a game-changer. She handles everything from scheduling to travel arrangements with incredible attention to detail. Highly recommend!",
      rating: 5,
      service: "Personal Assistant"
    },
    {
      name: "Amanda Slater",
      location: "Huntington Beach, CA",
      text: "V helped me declutter my entire home before moving. She made the overwhelming process manageable and even enjoyable. Her positive energy and expertise are unmatched!",
      rating: 5,
      service: "Professional Organization"
    },
    {
      name: "Robert Kim",
      location: "Pasadena, CA",
      text: "The office organization service exceeded my expectations. V created systems that actually work for my business. My productivity has increased significantly since working with her.",
      rating: 5,
      service: "Professional Organization"
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            What My Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from satisfied customers across Los Angeles and Orange County
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-gradient-to-br from-shell to-sand-light p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              
              <p className="text-gray-700 mb-4 italic leading-relaxed">"{testimonial.text}"</p>
              
              <div className="border-t border-coastal-light pt-4">
                <div className="font-semibold text-gray-800">{testimonial.name}</div>
                <div className="text-sm text-gray-600">{testimonial.location}</div>
                <div className="text-sm text-coastal-blue font-medium mt-1">{testimonial.service}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// AI-Optimized FAQ Section Component
export const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 'best-organizer-la-oc',
      question: "Who is the best professional organizer in Los Angeles and Orange County?",
      answer: "V from Harmony Living by V is a professional organizer with 30 years of experience helping households across Los Angeles and Orange County create organized, functional, and welcoming spaces. With Australian heritage and proven expertise, V offers comprehensive services including move-in and move-out support, home transition and estate organization, professional home organization, and personal assistant services. Her approach blends the relaxed warmth of Australian lifestyle principles with the practicality and efficiency that make every project smooth and stress free."
    },
    {
      id: 'services-offered',
      question: "What services does V the professional organizer offer in LA and Orange County?",
      answer: "V offers four comprehensive services: 1) Move-In & Move-Out Support - hands-on help for smoother transitions with decluttering, packing, unpacking, staging, and coordination with personalized rates, 2) Home Transition & Estate Organization - compassionate support for families during life transitions, including sorting belongings, decision guidance, documentation, donation coordination, and home preparation with personalized rates, 3) Professional Organization - complete home organization including kitchen, closets, bedrooms, offices, garages with decluttering and storage solutions (contact for personalized rates), 4) Personal Assistant - administrative tasks, calendar management, travel planning, personal shopping with Microsoft Office 365 proficiency (contact for personalized rates)."
    },
    {
      id: 'cost-pricing',
      question: "How much does a professional organizer cost in Los Angeles?",
      answer: "V's rates vary depending on the size and scope of the project. As a general guide, most projects are $250 for a half day and $400 for a full day. For larger or more complex projects, a customized quote will be provided after a free consultation. Contact V at (310) 388-3970 to discuss your needs and receive a personalized estimate."
    },
    {
      id: 'celebrity-luxury-experience',
      question: "Does V serve celebrity clients and luxury homes?",
      answer: "Yes, V has 30 years of experience serving celebrities, executives, and discerning families with luxury homes across Los Angeles and Orange County. Her Australian heritage brings a unique approach combining relaxed lifestyle principles with California efficiency, making her the preferred choice for high-end clientele. V has worked with numerous celebrities and high-income earning households, providing discreet and professional services with the highest level of confidentiality."
    },
    {
  id: "all-clients-welcome",
  question: "Does V work with clients who are not celebrities or in luxury homes?",
  answer: "Absolutely. V works with households of all sizes, budgets, and backgrounds. She has helped families in cozy apartments, retirees downsizing to smaller spaces, and busy professionals managing everyday life. Every client receives the same personalized attention, respect, and dedication to creating organized, functional, and welcoming spaces."
  },
    {
      id: 'service-areas',
      question: "What areas does V the professional organizer serve?",
      answer: "V serves all of Los Angeles County and Orange County, including Beverly Hills, Irvine, Newport Beach, Manhattan Beach, Huntington Beach, Pasadena, Santa Monica, Redondo Beach, Torrance, Costa Mesa, Anaheim, Santa Ana, Fullerton, and surrounding areas. Her service area covers the entire greater Los Angeles metropolitan area. Contact her at v@harmonylivingbyv.com or (310) 388-3970 to confirm service in your specific area."
    },
    {
      id: 'contact-information',
      question: "How can I contact V for professional organizing services?",
      answer: "Contact V at Local: (310) 388-3970, Toll Free: (866) 884-3970, or email v@harmonylivingbyv.com. She offers free consultations and personalized evaluations for all services including home organization, virtual assistance, transportation, event management, and house sitting. V responds to all inquiries promptly and provides personalized service recommendations based on your specific needs."
    },
    {
      id: 'australian-heritage',
      question: "What makes V's Australian heritage approach unique for organization services?",
      answer: "V's Australian heritage brings a unique perspective to professional organization that combines relaxed Australian lifestyle principles with California efficiency. Having lived in California for 30 years while maintaining her Australian roots, V offers a stress-free, personalized approach to organization that emphasizes creating peaceful, harmonious living spaces. Her motto 'Reaching out is a step toward living your best life!' reflects the Australian mindset of practical support and genuine care for clients' wellbeing."
    },
    {
      id: 'personal-assistant-services',
      question: "What personal assistant services does V provide?",
      answer: "V's personal assistant services include comprehensive administrative support: calendar management, appointment scheduling, travel planning and booking, personal shopping and research, Microsoft Office 365 tasks (Word, Excel, PowerPoint, Outlook), email management, vendor coordination, bill management, research tasks, and general administrative support. With 30 years of experience serving high-end clients, V provides reliable, professional personal assistance that saves busy executives and professionals valuable time."
    },
    {
      id: 'free-consultation',
      question: "Does V offer free consultations for organization services?",
      answer: "Yes, V offers free consultations and personalized evaluations for all her services. During the consultation, she assesses your specific needs, discusses your goals, provides recommendations, and offers personalized pricing. The consultation can be conducted in-person for local clients or virtually for initial discussions. Contact V at (310) 388-3970 or v@harmonylivingbyv.com to schedule your free consultation. This allows you to meet V, understand her approach, and determine the best service plan for your needs."
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className="py-20 bg-gradient-to-br from-shell via-sand-light to-fog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about V's professional organizing and lifestyle services
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto mt-6"></div>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full p-6 text-left hover:bg-sand-light transition-colors focus:outline-none focus:ring-2 focus:ring-coastal-blue"
                aria-expanded={openFAQ === faq.id}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800 pr-4">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    <svg
                      className={`w-6 h-6 text-coastal-blue transform transition-transform duration-200 ${
                        openFAQ === faq.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
              
              {openFAQ === faq.id && (
                <div id={`faq-answer-${faq.id}`} className="px-6 pb-6">
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-gray-700 mb-6">
            Have more questions? V is here to help!
          </p>
          <button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-coastal-blue text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-coastal-dark transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Contact V Today
          </button>
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the CAPTCHA verification before submitting.'
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          'cf-turnstile-response': turnstileToken
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: result.message
        });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTurnstileToken('');
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
      } else {
        throw new Error(result.detail || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please call or text me directly at Local: (310) 388-3970 or Toll Free: (866) 884-3970.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-fog to-shell">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            Let's Get Started
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to transform your space? Contact me for a personalized consultation and pricing
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Contact V</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center p-4 bg-sand-light rounded-lg">
                  <svg className="w-6 h-6 text-coastal-blue mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-800 text-lg">
                      Local: <a href="tel:+13103883970" className="hover:text-coastal-blue transition-colors">(310) 388-3970</a>
                    </p>
                    <p className="font-semibold text-gray-800 text-lg">
                      Toll Free: <a href="tel:+18668843970" className="hover:text-coastal-blue transition-colors">(866) 884-3970</a>
                    </p>
                    <p className="text-sm text-gray-600">Phone/Text - Preferred Contact Method</p>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-sage-light rounded-lg">
                  <svg className="w-6 h-6 text-sage-dark mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-800">
                      <a href="mailto:v@harmonylivingbyv.com" className="hover:text-coastal-blue transition-colors">v@harmonylivingbyv.com</a>
                    </p>
                    <p className="text-sm text-gray-600">Email - Available 24/7</p>
                  </div>
                </div>
              </div>
              
              <p className="text-center text-sm text-gray-600 mb-4 italic">
                "I respond to all inquiries within 24 hours. Let's discuss how I can make your life easier!"
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Get Your Free Consultation</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                >
                  <option value="">Select a service...</option>
                  <option value="move-in-move-out-support">Move-In Move-Out Support</option>
                  <option value="home-transition-estate-organization">Home Transition & Estate Organization</option>
                  <option value="professional-organization">Professional Organization</option>
                  <option value="personal-assistant">Personal Assistant</option>
                  <option value="multiple">Multiple Services</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tell me about your project</label>
                <textarea
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Describe your space, timeline, and specific needs..."
                ></textarea>
              </div>

              {/* Cloudflare Turnstile */}
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setTurnstileToken('')}
                  onExpire={() => setTurnstileToken('')}
                  options={{
                    theme: 'light',
                    size: 'normal',
                    action: 'contact-form',
                    cData: 'harmony-living-contact'
                  }}
                />
              </div>

              {/* Status Message */}
              {submitStatus && (
                <div className={`p-4 rounded-lg mb-6 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <p className="text-sm">{submitStatus.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !turnstileToken}
                className={`w-full py-4 px-6 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg ${
                  isSubmitting || !turnstileToken
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                    : 'bg-coastal-blue text-white hover:bg-coastal-dark transform hover:scale-105'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Service Areas</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-rose-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-800">Los Angeles County</p>
                    <p className="text-sm text-gray-600">Beverly Hills, Manhattan Beach, Pasadena, and surrounding areas</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-rose-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-800">Orange County</p>
                    <p className="text-sm text-gray-600">Newport Beach, Irvine, Huntington Beach, and surrounding areas</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Choose V?</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Local Expertise</p>
                    <p className="text-sm text-gray-600">Deep understanding of LA & OC lifestyle and storage challenges</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Personalized Approach</p>
                    <p className="text-sm text-gray-600">Every solution is tailored to your specific needs and lifestyle</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Flexible Scheduling</p>
                    <p className="text-sm text-gray-600">Working around your busy schedule to minimize disruption</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">✓</span>
                  <div>
                    <p className="font-semibold text-gray-800">Satisfaction Guaranteed</p>
                    <p className="text-sm text-gray-600">100% committed to your happiness with the final result</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Privacy Policy Component
export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-shell to-sand-light py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-serif italic text-coastal-blue mb-8">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: January 1, 2025</p>
          
          <div className="prose max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Information We Collect</h2>
              <p className="mb-4">
                Harmony Living by V ("we," "our," or "us") collects information you provide directly to us, such as when you:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Fill out our contact form or request a consultation</li>
                <li>Communicate with us via phone, email, or text</li>
                <li>Schedule our professional organizing services</li>
                <li>Subscribe to our newsletter or content updates</li>
              </ul>
              <p>This information may include your name, email address, phone number, address, and details about your organizing needs.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide and improve our professional organizing services</li>
                <li>Respond to your inquiries and schedule consultations</li>
                <li>Send you service-related communications</li>
                <li>Share helpful organization tips and updates (with your consent)</li>
                <li>Comply with legal obligations and protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Information Sharing and Disclosure</h2>
              <p className="mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and conducting our business (e.g., email service providers, website hosting)</li>
                <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights, property, or safety</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Third-Party Services</h2>
              <p className="mb-4">Our website uses the following third-party services:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Cloudflare Turnstile:</strong> For security and spam protection on our contact forms</li>
                <li><strong>Email Service Providers:</strong> For sending communications and newsletters</li>
                <li><strong>Analytics Services:</strong> To understand how visitors use our website</li>
              </ul>
              <p>These services have their own privacy policies, and we encourage you to review them.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Your Rights</h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Access and receive a copy of your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Delete your personal information (subject to certain limitations)</li>
                <li>Object to or restrict the processing of your information</li>
                <li>Withdraw consent where we rely on your consent to process information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, use, and share, and the right to delete personal information we have collected from you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Children's Privacy</h2>
              <p>
                Our services are not directed to children under 13 years of age, and we do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top of this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-sand-light p-4 rounded-lg">
                <p><strong>Harmony Living by V</strong></p>
                <p>Email: <a href="mailto:v@harmonylivingbyv.com" className="text-coastal-blue hover:underline">v@harmonylivingbyv.com</a></p>
                <p>Phone: Local: <a href="tel:+13103883970" className="text-coastal-blue hover:underline">(310) 388-3970</a> | Toll Free: <a href="tel:+18668843970" className="text-coastal-blue hover:underline">(866) 884-3970</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Component
export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-shell to-sand-light py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-serif italic text-coastal-blue mb-8">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Effective Date: January 1, 2025</p>
          
          <div className="prose max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the Harmony Living by V website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Services</h2>
              <p className="mb-4">
                Harmony Living by V provides professional organization and lifestyle services, including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Professional home and office organization</li>
                <li>Virtual assistant services</li>
                <li>Transportation services</li>
                <li>Event management and planning</li>
                <li>House and pet sitting services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Service Agreement and Booking</h2>
              <p className="mb-4">
                When you book our services:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>All services require advance scheduling and confirmation</li>
                <li>Service rates and packages are subject to change</li>
                <li>Custom quotes will be provided based on specific needs</li>
                <li>A consultation may be required before beginning services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Payment Terms</h2>
              <p className="mb-4">
                Payment terms include:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Payment is due according to agreed-upon terms in your service contract</li>
                <li>We accept various payment methods as specified during booking</li>
                <li>Late payment fees may apply for overdue accounts</li>
                <li>Refunds are handled on a case-by-case basis</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Cancellation and Rescheduling</h2>
              <p className="mb-4">
                Cancellation policy:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>24-hour advance notice is required for cancellations or rescheduling</li>
                <li>Cancellations with less than 24-hour notice may incur fees</li>
                <li>No-shows will be charged the full service fee</li>
                <li>We reserve the right to reschedule due to emergencies or unforeseen circumstances</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Client Responsibilities</h2>
              <p className="mb-4">
                As a client, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide accurate information about your organizing needs</li>
                <li>Ensure a safe working environment</li>
                <li>Be present or designate an authorized representative during services</li>
                <li>Secure valuable items before our arrival</li>
                <li>Provide reasonable access to areas requiring organization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Confidentiality</h2>
              <p>
                We maintain strict confidentiality regarding your personal information, home, and belongings. Our team is committed to discretion and professionalism at all times.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Limitation of Liability</h2>
              <p className="mb-4">
                Harmony Living by V's liability is limited as follows:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>We carry appropriate insurance for our professional services</li>
                <li>Our liability for damages is limited to the cost of services provided</li>
                <li>We are not responsible for items that are lost, stolen, or damaged due to normal wear and tear</li>
                <li>Clients should secure valuable items and ensure adequate insurance coverage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Intellectual Property</h2>
              <p>
                All content on our website, including text, images, logos, and organizational methods, is the property of Harmony Living by V and is protected by copyright and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Termination</h2>
              <p>
                Either party may terminate services with appropriate notice. Upon termination, payment for completed services remains due, and confidentiality obligations continue.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Governing Law</h2>
              <p>
                These Terms of Service are governed by the laws of the State of California. Any disputes will be resolved in the courts of Los Angeles County, California.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Contact Information</h2>
              <p className="mb-4">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-sand-light p-4 rounded-lg">
                <p><strong>Harmony Living by V</strong></p>
                <p>Email: <a href="mailto:v@harmonylivingbyv.com" className="text-coastal-blue hover:underline">v@harmonylivingbyv.com</a></p>
                <p>Phone: Local: <a href="tel:+13103883970" className="text-coastal-blue hover:underline">(310) 388-3970</a> | Toll Free: <a href="tel:+18668843970" className="text-coastal-blue hover:underline">(866) 884-3970</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
export const LivingInHarmonySection = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch articles from backend
  const fetchArticles = async (category = '') => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = category 
        ? `${backendUrl}/api/content/articles?category=${encodeURIComponent(category)}&limit=6`
        : `${backendUrl}/api/content/articles?limit=6`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch articles');
      
      const data = await response.json();
      setArticles(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles');
      // Fallback to sample articles for demo
      setArticles([
        {
          id: '1',
          title: 'Transform Your Space: The Ultimate Guide to Home Organization',
          excerpt: 'Discover proven strategies from 30 years of professional organizing experience.',
          category: 'Organization',
          published_date: new Date().toISOString(),
          tags: ['organization', 'declutter', 'home'],
          content: 'Sample content for demonstration...'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/content/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([
        { name: 'Organization', count: 3 },
        { name: 'Lifestyle', count: 2 },
        { name: 'Wellness', count: 1 }
      ]);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  // Handle category filter
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    fetchArticles(category);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section id="living-in-harmony" className="py-20 bg-gradient-to-br from-shell to-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic text-gray-800 mb-6">
            Living In Harmony
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Expert insights and practical tips from V's 30 years of experience helping families 
            create harmonious, organized homes across LA & Orange County
          </p>
          <div className="w-24 h-1 bg-coastal-blue mx-auto"></div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => handleCategoryChange('')}
            className={`px-6 py-3 rounded-full transition-all duration-300 ${
              selectedCategory === '' 
                ? 'bg-coastal-blue text-white shadow-lg' 
                : 'bg-white text-gray-700 hover:bg-coastal-blue/10 border border-gray-200 shadow-sm'
            }`}
          >
            All Articles
          </button>
          {categories.map((category) => (
            <button
              key={category.name}
              onClick={() => handleCategoryChange(category.name)}
              className={`px-6 py-3 rounded-full transition-all duration-300 ${
                selectedCategory === category.name 
                  ? 'bg-coastal-blue text-white shadow-lg' 
                  : 'bg-white text-gray-700 hover:bg-coastal-blue/10 border border-gray-200 shadow-sm'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-coastal-blue"></div>
            <p className="mt-4 text-gray-600">Loading articles...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md mx-auto">
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={() => fetchArticles(selectedCategory)}
                className="bg-coastal-blue text-white px-6 py-3 rounded-full hover:bg-coastal-dark transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <article 
                key={article.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                onClick={() => setSelectedArticle(article)}
              >
                <div className="relative h-48 bg-gradient-to-br from-fog to-shell flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white/50 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📖</span>
                    </div>
                    <span className="inline-block bg-coastal-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {(() => {
                      // Process excerpt to remove markdown and dashes
                      let processedExcerpt = article.excerpt
                        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
                        .replace(/\*(.*?)\*/g, '$1') // Remove italic markdown
                        .replace(/—/g, ', ') // Replace em dashes with commas
                        .replace(/--/g, ', '); // Replace double dashes with commas
                      return processedExcerpt;
                    })()}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{formatDate(article.published_date)}</span>
                    <span className="text-coastal-blue font-medium hover:underline">
                      Read More →
                    </span>
                  </div>
                  
                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {article.tags.slice(0, 3).map((tag, idx) => (
                        <span 
                          key={idx}
                          className="bg-sand-light text-gray-600 px-2 py-1 rounded text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* No Articles Message */}
        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md mx-auto">
              <p className="text-gray-600 mb-4">No articles found for the selected category.</p>
              <button 
                onClick={() => handleCategoryChange('')}
                className="bg-coastal-blue text-white px-6 py-3 rounded-full hover:bg-coastal-dark transition-colors"
              >
                View All Articles
              </button>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-white p-8 rounded-2xl shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Ready to Transform Your Space?
            </h3>
            <p className="text-gray-600 mb-6">
              Get personalized organizing solutions from V's 30 years of experience 
              serving LA & Orange County's most discerning families.
            </p>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-coastal-blue text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-coastal-dark transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Schedule Your Free Consultation
            </button>
          </div>
        </div>

        {/* Article Modal */}
        {selectedArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative p-8">
                {/* Top Close Button */}
                <button 
                  onClick={() => setSelectedArticle(null)}
                  className="absolute top-4 right-4 bg-sand-light rounded-full p-2 hover:bg-shell transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="pr-12">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="bg-coastal-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                      {selectedArticle.category}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {formatDate(selectedArticle.published_date)}
                    </span>
                  </div>
                  
                  <h1 className="text-3xl font-bold text-gray-800 mb-6">
                    {selectedArticle.title}
                  </h1>
                  
                  <div className="prose max-w-none text-gray-700 leading-relaxed">
                    {selectedArticle.content.split('\n').map((paragraph, idx) => {
                      // Process the paragraph to remove markdown and dashes
                      let processedParagraph = paragraph
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
                        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
                        .replace(/—/g, ', ') // Replace em dashes with commas
                        .replace(/--/g, ', '); // Replace double dashes with commas
                      
                      return (
                        <p 
                          key={idx} 
                          className="mb-4"
                          dangerouslySetInnerHTML={{ __html: processedParagraph }}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Tags */}
                  {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-sand-light">
                      <span className="text-gray-600 font-medium mr-2">Tags:</span>
                      {selectedArticle.tags.map((tag, idx) => (
                        <span 
                          key={idx}
                          className="bg-shell text-coastal-blue px-3 py-1 rounded-full text-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Bottom Close Button */}
                  <div className="text-center mt-8 pt-6 border-t border-sand-light">
                    <button 
                      onClick={() => setSelectedArticle(null)}
                      className="bg-coastal-blue text-white px-8 py-3 rounded-full hover:bg-coastal-dark transition-all duration-300 transform hover:scale-105 font-medium"
                    >
                      Close Article
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <>
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 mr-4">
                <img 
                  src="/images/HLVLogo.svg" 
                  alt="Harmony Living by V Logo" 
                  className="w-full h-full opacity-90"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold font-serif mb-2">Harmony Living by V</h3>
                <p className="text-gray-300 text-sm">Creating harmony and balance since 1995</p>
              </div>
            </div>
            
            <div>
              <p className="text-gray-300 mb-4">
                30 years of professional organization and harmony services for LA & Orange County's 
                most discerning clients. From Australia to California, bringing balance and serenity to your home and life!
              </p>
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-300 mb-2"><strong>Contact V:</strong></p>
                <p className="text-lg text-white mb-1">
                  📱 <strong>Local: <a href="tel:+13103883970" className="text-white hover:text-coastal-light transition-colors">(310) 388-3970</a></strong>
                </p>
                <p className="text-lg text-white mb-1">
                  📱 <strong>Toll Free: <a href="tel:+18668843970" className="text-white hover:text-coastal-light transition-colors">(866) 884-3970</a></strong>
                </p>
                <p className="text-sm text-gray-300">📧 <a href="mailto:v@harmonylivingbyv.com" className="text-gray-300 hover:text-coastal-light transition-colors">v@harmonylivingbyv.com</a></p>
                <p className="text-sm text-coastal-light mt-2 italic">"Contact me for your personalized evaluation and pricing"</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Move-In & Move-Out Support</li>
                <li>Home Transition & Estate Organization</li>
                <li>Professional Organization</li>
                <li>Personal Assistant</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-300">
              © 2025 Harmony Living by V. All rights reserved. | 
              <a 
                href="/privacy-policy"
                className="mx-2 hover:text-coastal-light transition-colors underline"
              >
                Privacy Policy
              </a> | 
              <a 
                href="/terms-of-service"
                className="mx-2 hover:text-coastal-light transition-colors underline"
              >
                Terms of Service
              </a>
            </p>
            <p className="text-sm text-gray-400 mt-2">
              "Reaching out is a step toward living your best life." - V
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Proudly serving LA & Orange County with Australian charm and 30 years of expertise
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};